import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Loader2, FileText, Download, Trash2, Pencil, Trophy, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RaceClassRecordDialog from '../../components/horses/RaceClassRecordDialog';
import RacingFormDialog from '../../components/horses/RacingFormDialog';
import { useConfirm } from '../../components/common/ConfirmDialog';

const RACING_FORM_TYPES = [
  { value: 'pdf_racing_form_ragozin', label: 'Ragozin' },
  { value: 'pdf_racing_form_beyer', label: 'Beyer' },
  { value: 'pdf_racing_form_thoroughmanager', label: 'Thoroughmanager' },
  { value: 'pdf_racing_form_lifetime_pp', label: 'Lifetime Past Performance' },
];

const RacingTab = ({ horse }) => {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const horseId = horse?.id;

  const { data: raceRecords = [], isLoading: loading } = useQuery({
    queryKey: QK.raceRecords(horseId),
    queryFn: () => api.get(`/race-class-records?horse_id=${horseId}`).then(r => r.data),
    enabled: !!horseId,
  });

  const { data: progenyAlerts = [], refetch: refetchAlerts } = useQuery({
    queryKey: QK.progenyAlerts(horseId),
    queryFn: () => api.get(`/race-class-records/progeny-alerts/${horseId}`).then(r => r.data),
    enabled: !!horseId,
  });

  const { data: racingForms = [] } = useQuery({
    queryKey: QK.entityFiles({ horse_id: horseId, category_prefix: 'pdf_racing_form' }),
    queryFn: async () => {
      const r = await api.get('/files', { params: { horse_id: horseId } });
      return (r.data || []).filter(f => f.category?.startsWith('pdf_racing_form'));
    },
    enabled: !!horseId,
  });

  const invalidateRacing = () => qc.invalidateQueries({ queryKey: QK.raceRecords(horseId) });
  const invalidateForms  = () => qc.invalidateQueries({ queryKey: QK.entityFiles({ horse_id: horseId, category_prefix: 'pdf_racing_form' }) });

  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordDetailOpen, setRecordDetailOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [editFileData, setEditFileData] = useState({ description: '', notes: '', category: '' });

  const dismissProgenyAlert = async (alertKey) => {
    try {
      await api.post(`/race-class-records/progeny-alerts/${horseId}/dismiss`, { alert_key: alertKey });
      refetchAlerts();
    } catch { toast.error('Failed to dismiss'); }
  };

  const handleRecordCreated = async () => {
    setRecordDialogOpen(false);
    setEditingRecord(null);
    invalidateRacing();
    toast.success('Race class record saved successfully');
  };

  const handleFormUploaded = () => {
    setFormDialogOpen(false);
    invalidateForms();
    toast.success('Racing form uploaded successfully');
  };

  const handleDeleteRecord = async (recordId) => {
    if (!await confirm({ title: 'Are you sure you want to delete this race class record?', destructive: true })) return;

    try {
      await api.delete(`/race-class-records/${recordId}`);
      toast.success('Race class record deleted');
      invalidateRacing();
    } catch (error) {
      console.error('Error deleting record:', error);
      toast.error('Failed to delete race class record');
    }
  };

  const handleRecordClick = (record, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedRecord(record);
    setRecordDetailOpen(true);
  };

  const handleEditRecord = () => {
    setEditingRecord(selectedRecord);
    setRecordDetailOpen(false);
    setRecordDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFileClick = async (file, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setPreviewFile(file);
    setEditFileData({
      description: file.description || '',
      notes: file.notes || '',
      category: file.category || ''
    });
    setIsEditingFile(false);
    setPreviewOpen(true);
    
    try {
      const response = await api.get(`/files/${file.id}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error loading file preview:', error);
      toast.error('Failed to load file preview');
    }
  };

  const handleUpdateFile = async () => {
    try {
      const formData = new FormData();
      formData.append('description', editFileData.description);
      formData.append('notes', editFileData.notes);
      formData.append('category', editFileData.category);

      await api.put(`/files/${previewFile.id}`, formData);
      
      toast.success('File updated successfully');
      setIsEditingFile(false);
      invalidateForms();
      
      // Update preview file data
      setPreviewFile({
        ...previewFile,
        description: editFileData.description,
        notes: editFileData.notes,
        category: editFileData.category
      });
    } catch (error) {
      console.error('Error updating file:', error);
      toast.error('Failed to update file');
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/files/${fileId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Download failed');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!await confirm({ title: 'Are you sure you want to delete this file?', destructive: true })) return;

    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted successfully');
      invalidateForms();
      if (previewFile?.id === fileId) {
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progeny Alerts */}
      {progenyAlerts.length > 0 && (
        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-600" />
              Progeny & Family Alerts
              <Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">{progenyAlerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {progenyAlerts.map(alert => (
              <div key={alert.id} className="flex items-start gap-3 p-3 border rounded bg-yellow-50/50">
                <Trophy className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    {alert.sibling_name} — Grade 1 Win
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {alert.race_name} at {alert.racetrack} ({alert.race_date ? new Date(alert.race_date).toLocaleDateString() : ''})
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {alert.relationship} • Result: {alert.race_result}
                    {alert.race_purse ? ` • Purse: $${alert.race_purse.toLocaleString()}` : ''}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => dismissProgenyAlert(alert.id)} title="Dismiss">
                  <X className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Race Class Records Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Best Race Class Records</CardTitle>
            <Button
              onClick={() => setRecordDialogOpen(true)}
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {raceRecords.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No race class records yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {raceRecords.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={(e) => handleRecordClick(record, e)}
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{record.race_class}{record.race_result ? ` (${record.race_result})` : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      Race Date: {formatDate(record.race_date)}
                    </p>
                  </div>
                </div>
              ))}
              {raceRecords.length > 5 && (
                <div className="pt-4 flex justify-center">
                  <Button variant="outline" size="sm">
                    <Loader2 className="w-4 h-4 mr-2" /> Load more...
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Numbers / Racing Forms Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Performance Numbers</CardTitle>
            <Button
              onClick={() => setFormDialogOpen(true)}
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {racingForms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No racing forms yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {racingForms.map((form) => (
                <div
                  key={form.id}
                  className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={(e) => handleFileClick(form, e)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-sm">
                        {form.category?.replace('pdf_racing_form_', '').replace(/_/g, ' ').toUpperCase()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added: {formatDate(form.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {racingForms.length > 5 && (
                <div className="pt-4 flex justify-center">
                  <Button variant="outline" size="sm">
                    <Loader2 className="w-4 h-4 mr-2" /> Load more...
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <RaceClassRecordDialog
        open={recordDialogOpen}
        onClose={() => {
          setRecordDialogOpen(false);
          setEditingRecord(null);
        }}
        onSuccess={handleRecordCreated}
        horse={horse}
        record={editingRecord}
      />

      <RacingFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSuccess={handleFormUploaded}
        horse={horse}
      />

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={(open) => {
        setPreviewOpen(open);
        if (!open) {
          setIsEditingFile(false);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between">
            {isEditingFile ? (
              <div className="flex-1 mr-4 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Description / Name</Label>
                  <Input
                    value={editFileData.description}
                    onChange={(e) => setEditFileData({ ...editFileData, description: e.target.value })}
                    placeholder="Enter description"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={editFileData.category} onValueChange={(value) => setEditFileData({ ...editFileData, category: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {RACING_FORM_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    value={editFileData.notes}
                    onChange={(e) => setEditFileData({ ...editFileData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <DialogTitle>{previewFile?.description || previewFile?.original_filename}</DialogTitle>
                {previewFile?.category && (
                  <p className="text-xs text-primary mt-1">
                    {RACING_FORM_TYPES.find(t => t.value === previewFile.category)?.label || previewFile.category}
                  </p>
                )}
                {previewFile?.notes && (
                  <p className="text-xs text-muted-foreground mt-1">{previewFile.notes}</p>
                )}
              </div>
            )}
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[400px]">
            {previewUrl ? (
              previewFile?.mime_type?.includes('pdf') ? (
                <iframe 
                  src={previewUrl} 
                  title="PDF Preview"
                  className="w-full h-[60vh] border rounded"
                />
              ) : (
                <p className="text-muted-foreground">Preview not available for this file type</p>
              )
            ) : (
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            )}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <div className="flex gap-2">
              {isEditingFile ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsEditingFile(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateFile}>
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setIsEditingFile(true)}
                    title="Edit file details"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => handleDeleteFile(previewFile?.id)}
                    className="text-destructive hover:text-destructive"
                    title="Delete file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
            {!isEditingFile && (
              <Button 
                onClick={() => handleDownload(previewFile?.id, previewFile?.original_filename)}
              >
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Race Class Record Detail Dialog */}
      <Dialog open={recordDetailOpen} onOpenChange={setRecordDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Race Class Record Details</DialogTitle>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Race Name</Label>
                  <p className="font-medium">{selectedRecord.race_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Race Date</Label>
                  <p className="font-medium">{formatDate(selectedRecord.race_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Race Class</Label>
                  <p className="font-medium">{selectedRecord.race_class}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Result</Label>
                  <p className="font-medium">{selectedRecord.race_result}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Racetrack</Label>
                  <p className="font-medium">{selectedRecord.racetrack}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Age at Race</Label>
                  <p className="font-medium">{selectedRecord.age_at_race || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Race Distance</Label>
                  <p className="font-medium">{selectedRecord.race_distance || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Race Surface</Label>
                  <p className="font-medium">{selectedRecord.race_surface || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Race Purse</Label>
                  <p className="font-medium">
                    {selectedRecord.race_purse 
                      ? `${selectedRecord.race_purse_currency} ${selectedRecord.race_purse.toLocaleString()}`
                      : '-'
                    }
                  </p>
                </div>
              </div>
              {selectedRecord.other_race_notes && (
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1">{selectedRecord.other_race_notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleEditRecord}
              title="Edit record"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => setRecordDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RacingTab;
